/** A surface with a hairline edge.
 *
 * On this ground a cast shadow is invisible, so elevation is an outline: `sm`
 * is a single neutral hairline, `md` adds ambient darkness for something that
 * has just appeared (the token reveal), `lg` is for a dialog. A card that is
 * itself a control — a session in the list, an issue in the inbox — takes
 * `selected`, which swaps the hairline for an accent ring rather than tinting
 * the surface, because the surface is already the only light in the row.
 */

import type { CSSProperties, ReactNode } from 'react';

export interface CardProps {
  children: ReactNode;
  elevation?: 'none' | 'sm' | 'md' | 'lg';
  /** Rings the card in the accent instead of the neutral hairline. */
  selected?: boolean;
  className?: string;
  style?: CSSProperties;
  /** Renders a `section` with this as its accessible name. */
  label?: string;
  as?: 'div' | 'section' | 'article' | 'aside';
}

const ELEVATION: Record<NonNullable<CardProps['elevation']>, string> = {
  none: '',
  sm: 'elev-sm',
  md: 'elev-md',
  lg: 'elev-lg',
};

export function Card({
  children,
  elevation = 'sm',
  selected,
  className,
  style,
  label,
  as: Tag = 'div',
}: CardProps) {
  return (
    <Tag
      aria-label={label}
      className={['card', selected ? '' : ELEVATION[elevation], className ?? '']
        .filter(Boolean)
        .join(' ')}
      style={selected ? { boxShadow: '0 0 0 1px var(--color-accent)', ...style } : style}
    >
      {children}
    </Tag>
  );
}

export interface CardTitleProps {
  children: ReactNode;
  /** Clips to one line with an ellipsis — circuit names are long. */
  truncate?: boolean;
  className?: string;
  as?: 'div' | 'h2' | 'h3';
}

export function CardTitle({ children, truncate, className, as: Tag = 'div' }: CardTitleProps) {
  return (
    <Tag
      className={['card-title', truncate ? 'truncate' : '', className ?? ''].filter(Boolean).join(' ')}
      title={truncate && typeof children === 'string' ? children : undefined}
    >
      {children}
    </Tag>
  );
}

export interface CardMetaProps {
  children: ReactNode;
  className?: string;
}

/** The 11px line under a card: an id, a length, when it was published. */
export function CardMeta({ children, className }: CardMetaProps) {
  return <div className={['card-meta', className ?? ''].filter(Boolean).join(' ')}>{children}</div>;
}

export default Card;
